// paquetes
import { useNavigate, useOutletContext, useSearchParams, Link } from 'react-router-dom';
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from 'sweetalert2';
// componentes
import ZonaCargando from '../../alertas/zonaCargando';
// funciones
import { slugify } from "../../../funcionesDatos/Textos";
// estilos
import '../../../estilos/usuario.css';

const LIMITE = 10;
const MAX_RESPUESTA = 800;

function formatearFecha(valor) {
  if (!valor) return "";
  try {
    const d = new Date(valor);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("es-CO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(d);
  } catch (e) { return ""; }
}

function PreguntasVendedor() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const idServicioFiltro = searchParams.get("servicio") || null;

  const { usuario, cargandoUsuario } = useOutletContext() || {};

  const [cargando, setCargando]         = useState(false);
  const [preguntas, setPreguntas]       = useState([]);
  const [total, setTotal]               = useState(0);
  const [pendientes, setPendientes]     = useState(0);
  const [hayMas, setHayMas]             = useState(false);
  const [pagina, setPagina]             = useState(1);
  const [tab, setTab]                   = useState("pendientes"); // "pendientes" | "todas"
  const [nombreServicioFiltro, setNombreServicioFiltro] = useState("");

  // borrador de respuesta por pregunta: { [idPregunta]: texto }
  const [respuestas, setRespuestas]     = useState({});
  const [enviando, setEnviando]         = useState(null); // idPregunta en curso
  const [ocultando, setOcultando]       = useState(null); // idPregunta en curso

  const cargandoFinal = Boolean(cargandoUsuario) || Boolean(cargando);

  const cargar = useCallback(async (paginaPedida = 1) => {
    if (!usuario?.idUsuario) return;
    setCargando(true);
    try {
      const res = await axios.post("/preguntas-servicio/vendedor", {
        pagina: paginaPedida,
        limite: LIMITE,
        soloSinResponder: tab === "pendientes",
        ...(idServicioFiltro ? { idServicio: idServicioFiltro } : {}),
      }, { withCredentials: true });

      const data = res?.data || {};
      if (!data.ok) return;

      setPreguntas((prev) => (paginaPedida === 1 ? data.preguntas : [...prev, ...(data.preguntas || [])]));
      setTotal(Number(data.total) || 0);
      setPendientes(Number(data.pendientes) || 0);
      setHayMas(Boolean(data.hayMas));
      setPagina(paginaPedida);

      if (idServicioFiltro && paginaPedida === 1) {
        const primera = (data.preguntas || [])[0];
        if (primera?.nombreServicio) setNombreServicioFiltro(primera.nombreServicio);
      }
    } catch (err) {
      console.log("Error cargando preguntas del vendedor:", err);
    } finally {
      setCargando(false);
    }
  }, [usuario, tab, idServicioFiltro]);

  useEffect(() => {
    if (cargandoUsuario) return;
    if (!usuario) { navigate("/iniciar-sesion"); return; }
    cargar(1);
  }, [usuario, cargandoUsuario, tab, idServicioFiltro, cargar, navigate]);

  async function responder(idPregunta) {
    const texto = String(respuestas[idPregunta] || "").trim();
    if (texto.length < 1) {
      return Swal.fire({ icon: "warning", title: "Escribe una respuesta", text: "La respuesta no puede estar vacía." });
    }
    if (texto.length > MAX_RESPUESTA) {
      return Swal.fire({ icon: "warning", title: "Respuesta muy larga", text: `Máximo ${MAX_RESPUESTA} caracteres.` });
    }

    setEnviando(idPregunta);
    try {
      const res = await axios.post("/preguntas-servicio/responder", { idPregunta, respuesta: texto }, { withCredentials: true });
      const data = res?.data || {};

      if (!data.ok) {
        return Swal.fire({ icon: "error", title: "No se pudo publicar", text: data.mensaje || "Intenta más tarde." });
      }

      setRespuestas((prev) => { const n = { ...prev }; delete n[idPregunta]; return n; });

      if (data.enRevision) {
        Swal.fire({ icon: "info", title: "Respuesta en revisión", text: data.mensaje || "Se publicará en unos minutos." });
        // Sigue "pendiente" hasta que el cron la apruebe: solo marcamos el estado en la fila.
        setPreguntas((prev) => prev.map((q) => q.idPregunta === idPregunta
          ? { ...q, respuesta: "", respondida: false, respuestaEnRevision: true }
          : q));
        return;
      }

      Swal.fire({ icon: "success", title: "Respuesta publicada", text: "El comprador fue notificado." });
      setPendientes((n) => Math.max(0, n - 1));

      if (tab === "pendientes") {
        // Se responde -> sale de la lista de "sin responder"
        setPreguntas((prev) => prev.filter((q) => q.idPregunta !== idPregunta));
        setTotal((n) => Math.max(0, n - 1));
      } else {
        setPreguntas((prev) => prev.map((q) => q.idPregunta === idPregunta
          ? { ...q, respuesta: texto, respondida: true, respuestaEnRevision: false, fechaRespuesta: data.fechaRespuesta || new Date().toISOString() }
          : q));
      }
    } catch (err) {
      const msg = err?.response?.data?.mensaje || "Ocurrió un error al publicar tu respuesta.";
      Swal.fire({ icon: "error", title: "No se pudo publicar", text: msg });
    } finally {
      setEnviando(null);
    }
  }

  // Ver la publicación pública: el vendedor está en modo Vendedor, así que
  // primero lo pasamos a Comprador y luego navegamos (mismo flujo que misServicios).
  async function verServicioPublico(idServicio, nombre) {
    try {
      await axios.post("/cambiar-modo", {
        params: { usuario: usuario.username, modoActual: usuario.modo },
      }, { withCredentials: true });

      const nombreSeguro = (nombre && nombre !== "NA") ? nombre : "servicio";
      const slug = (await slugify(nombreSeguro)) || "servicio";
      window.location.href = "/servicio/" + slug + "-" + idServicio;
    } catch (err) {
      console.log("Error al cambiar modo y abrir el servicio:", err);
      Swal.fire({ icon: "error", title: "No pudimos abrir el servicio", text: "Intenta de nuevo." });
    }
  }

  // Ocultar / mostrar una pregunta en la publicación pública (no la borra).
  async function ocultarPregunta(idPregunta, ocultar) {
    setOcultando(idPregunta);
    try {
      const res = await axios.post("/preguntas-servicio/ocultar", { idPregunta, ocultar }, { withCredentials: true });
      const data = res?.data || {};
      if (!data.ok) {
        return Swal.fire({ icon: "error", title: "No se pudo actualizar", text: data.mensaje || "Intenta más tarde." });
      }
      setPreguntas((prev) => prev.map((q) => q.idPregunta === idPregunta
        ? { ...q, ocultaPorVendedor: !!data.ocultaPorVendedor }
        : q));
    } catch (err) {
      const msg = err?.response?.data?.mensaje || "Ocurrió un error.";
      Swal.fire({ icon: "error", title: "No se pudo actualizar", text: msg });
    } finally {
      setOcultando(null);
    }
  }

  function quitarFiltroServicio() {
    searchParams.delete("servicio");
    setSearchParams(searchParams, { replace: true });
    setNombreServicioFiltro("");
  }

  return (
    <div>
      {cargandoFinal && preguntas.length === 0 ? <ZonaCargando />
      : usuario ?
      <div className="d-flex flex-row">
        <div className="flex-grow-1 container-fluid contenedorAdaptador">

          {/* Título */}
          <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom border-dark">
            <div className="container">
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <h1 className="h2 text-dark mb-0">Preguntas</h1>
                {pendientes > 0 ? <span className="badge bg-danger rounded-pill">{pendientes} sin responder</span> : null}
              </div>
            </div>
          </div>

          <div className="container mb-5 pb-4">

            {/* Filtro por servicio activo */}
            {idServicioFiltro ? (
              <div className="alert alert-light border d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span>
                  <i className="bi bi-funnel me-1"></i>
                  Preguntas de: <strong>{nombreServicioFiltro || "este servicio"}</strong>
                </span>
                <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill" onClick={quitarFiltroServicio}>
                  Ver todas
                </button>
              </div>
            ) : null}

            {/* Tabs */}
            <ul className="nav nav-pills gap-2 mb-4">
              <li className="nav-item">
                <button type="button" className={`nav-link ${tab === "pendientes" ? "active" : "border border-primary text-primary bg-transparent"}`} onClick={() => { setTab("pendientes"); setPagina(1); }}>
                  Sin responder
                </button>
              </li>
              <li className="nav-item">
                <button type="button" className={`nav-link ${tab === "todas" ? "active" : "border border-primary text-primary bg-transparent"}`} onClick={() => { setTab("todas"); setPagina(1); }}>
                  Todas{tab === "todas" && total > 0 ? ` (${total})` : ""}
                </button>
              </li>
            </ul>

            {/* Lista */}
            {preguntas.length === 0 ? (
              <div className="alert alert-light border">
                {cargando ? "Cargando…" : tab === "pendientes" ? "No tienes preguntas sin responder." : "Aún no hay preguntas."}
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {preguntas.map((q) => (
                  <div key={q.idPregunta} className="card shadow-sm border-0" style={{ opacity: q.ocultaPorVendedor ? 0.6 : 1 }}>
                    <div className="card-body">

                      {/* Encabezado: usuario + servicio + fecha */}
                      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                        {q.avatarUsuario ? (
                          <img src={q.avatarUsuario} alt={q.usuarioPregunta} style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "1.5px solid var(--color-border)" }} />
                        ) : (
                          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--color-border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: "var(--gris-suave)", flexShrink: 0 }}>
                            {(q.usuarioPregunta || "?").charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-grow-1">
                          {q.usernameUsuario ? (
                            <Link to={`/${q.usernameUsuario}`} className="fw-semibold text-dark text-decoration-none">{q.usuarioPregunta}</Link>
                          ) : (
                            <span className="fw-semibold">{q.usuarioPregunta}</span>
                          )}
                          <small className="text-muted d-block">{formatearFecha(q.createdAt)}</small>
                        </div>
                        {q.idServicio ? (
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            {q.nombreServicio ? <span className="badge bg-light text-dark border">{q.nombreServicio}</span> : null}
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-decoration-none"
                              onClick={() => verServicioPublico(q.idServicio, q.nombreServicio)}
                            >
                              Ver servicio <i className="bi bi-box-arrow-up-right"></i>
                            </button>
                          </div>
                        ) : null}
                      </div>

                      {/* Pregunta */}
                      <p className="mb-2">{q.pregunta}</p>

                      {/* Respuesta / formulario */}
                      {q.respondida && q.respuesta ? (
                        <div className="ms-4 ps-2 border-start">
                          <small className="text-muted d-block">Tu respuesta · {formatearFecha(q.fechaRespuesta)}</small>
                          <p className="mb-0">{q.respuesta}</p>
                        </div>
                      ) : q.respuestaEnRevision ? (
                        <div className="ms-4 ps-2 border-start text-muted">
                          <i className="bi bi-hourglass-split me-1"></i>
                          Tu respuesta está en revisión y se publicará en unos minutos.
                        </div>
                      ) : (
                        <div>
                          <textarea
                            className="form-control"
                            rows={2}
                            maxLength={MAX_RESPUESTA}
                            placeholder="Escribe tu respuesta…"
                            value={respuestas[q.idPregunta] || ""}
                            onChange={(e) => setRespuestas((prev) => ({ ...prev, [q.idPregunta]: e.target.value }))}
                            disabled={enviando === q.idPregunta}
                          />
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
                            <small className="text-muted">
                              {(respuestas[q.idPregunta] || "").length}/{MAX_RESPUESTA} · No incluyas teléfonos, correos ni direcciones.
                            </small>
                            <button type="button" className="btn btn-primary btn-sm" onClick={() => responder(q.idPregunta)} disabled={enviando === q.idPregunta}>
                              {enviando === q.idPregunta ? "Publicando…" : "Responder"}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Ocultar / mostrar en la publicación pública */}
                      <div className="d-flex align-items-center gap-2 mt-3 pt-2 border-top">
                        {q.ocultaPorVendedor ? (
                          <>
                            <span className="badge bg-secondary">
                              <i className="bi bi-eye-slash me-1"></i>Oculta en el servicio
                            </span>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-decoration-none"
                              onClick={() => ocultarPregunta(q.idPregunta, false)}
                              disabled={ocultando === q.idPregunta}
                            >
                              {ocultando === q.idPregunta ? "…" : "Mostrar de nuevo"}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-decoration-none text-secondary"
                            onClick={() => ocultarPregunta(q.idPregunta, true)}
                            disabled={ocultando === q.idPregunta}
                          >
                            <i className="bi bi-eye-slash me-1"></i>
                            {ocultando === q.idPregunta ? "Ocultando…" : "Ocultar en el servicio"}
                          </button>
                        )}
                      </div>

                    </div>
                  </div>
                ))}
              </div>
            )}

            {hayMas ? (
              <div className="text-center mt-3">
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => cargar(pagina + 1)} disabled={cargando}>
                  {cargando ? "Cargando…" : "Ver más"}
                </button>
              </div>
            ) : null}

          </div>
        </div>
      </div>
      : null}
    </div>
  );
}

export default PreguntasVendedor;
